import { useComputed } from '@preact/signals';
import { h } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { useNewStore, usePlayerContext, useStyles } from '../../../../common/hooks';
import { secondsToHHMMSS } from '../../../../common/utils';
import { withBreakPoints } from '../../../hoc';
import { computeEPGSegmentsWidths, formatMarkers } from '../../../utils/timeline';
import { Slider, Titles } from '../../common';
import TimeDisplay from './TimeDisplay';
import Tooltip, { TYPE_AUTO } from './Tooltip';

import {
  EPG_PROGRAM_VISUAL_SIZE,
  SPRITES_METADATA_TITLE_PROGRAM_STYLES,
  SPRITES_METADATA_TITLE_VIDEO_STYLES,
  TIMELINE_TIME_HOUR_STYLE,
  TIMELINE_WRAPPER_STYLE,
  TOOLTIP_AUTO_CONTAINER_STYLES
} from './styles';

import {
  TIMESHIFTING_AUTO_CONTROLLER_NAME,
  TIMESHIFTING_AUTO_DURATION,
  TIMESHIFTING_CONTROLLER_NAME,
  TIMESHIFTING_TYPE_AUTO,
  TIMESHIFTING_TYPE_MANUEL
} from '../../../../../core/timeshifting/types';
import { asPercent } from '../../../../../utils';
import { EMPTY_THUMBNAIL, SpritesheetUtils } from '../../../../../utils/spritesheet';
import { FINGER_UI } from '../../../../common/constants';
import { useDialogContext } from '../../../../common/context/DialogContext';
import '../../../../common/theme/styles/sliders.css';
import Position from '../../../../common/utils/Position';
import { dateToHhS, timeToText } from '../../../../common/utils/time';
import { SLIDER_BUFFERED_COLOR, SLIDER_TIMELINE_LIVE_COLOR } from '../../../theme/colors';
import Pins from './highlights/Pins';
import LiveCursor from './LiveCursor';
import {
  computeSeekPos,
  computeSeekTime,
  findCurrentProgram,
  onStartSlide,
  onStopSlide,
  resolveBubbleLabel,
  resolveprimaryTrackStyles,
  resolveSliderStyle,
  setHoverTimeline
} from './utils';

const DEFAULT_THUMBNAIL_WIDTH = 128;
const DEFAULT_THUMBNAIL_HEIGHT = 72;
const TOOLTIP_SIZE = { default: { width: 79, height: 'max-content' }, medium: { width: 85 }, 'large+extraLarge': { width: 110 } };
const CURSOR_SIZE = 18;
const CURSOR_SIZE_EXTRA_SMALL = 14;

const STROKE_SIZE = 6;
const BIG_STROKE_SIZE = 10;

const MAX_TIMELINE_VALUE_SHOW = 97;

function Timeline({
  mobile,
  small,
  extraSmall,
  hidden,
  timeDisplayStyles = {}
}) {
  const player = usePlayerContext();
  const { isSeeking, hoverTimeline, UIVisible, displayType } = useNewStore('ui', player);
  const playbackStore = useNewStore('playback', player);
  const uiStore = useNewStore('ui', player);
  const { buffered } = playbackStore;
  const $currentTimeSec = useComputed(() => Math.trunc(uiStore.$userCurrentTime.value));
  const currentTimeSec = $currentTimeSec.value;
  const { imagesHL, pinsHovered, pinsPosition } = useNewStore('highlights', player);
  const {
    duration,
    spritesheets,
    expectedStartOverDuration,
    timeshifting: {
      type: timeshifting,
      startOverTimeshifting
    },
    broadcastedAt,
    programMarkers,
    imagesEpg,
    isEPGDirty,
    isHighlightable
  } = useNewStore('media', player);

  const dialogCtx = useDialogContext();
  const setStyles = useStyles();
  const tooltipSize = setStyles(TOOLTIP_SIZE);
  const isTimeshiftingAuto = TIMESHIFTING_TYPE_AUTO === timeshifting;
  const isTimeshiftingManuel = TIMESHIFTING_TYPE_MANUEL === timeshifting;
  const hasHighlights = isHighlightable && pinsPosition.length > 0;

  /* computedDuration is used to substitute duration with the Startover stream's expected duration */
  const computedDuration = isTimeshiftingAuto ? TIMESHIFTING_AUTO_DURATION : duration;
  const [computedBuffer, setComputedBuffer] = useState(0);
  const [time, setTime] = useState(currentTimeSec);
  const [seekTime, setSeekTime] = useState(0);
  const [seekTimePosition, setSeekTimePosition] = useState(0);
  const [spritesheetLength, setSpritesheetLength] = useState(0);
  const [thumbnail, setThumbnail] = useState(EMPTY_THUMBNAIL);
  const [segments, setSegments] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentSegment, setCurrentSegment] = useState(0);

  const [thumbnailSize, setThumbnailSize] = useState({
    width: isTimeshiftingAuto || hasHighlights ? DEFAULT_THUMBNAIL_WIDTH : 0,
    height: isTimeshiftingAuto || hasHighlights ? DEFAULT_THUMBNAIL_HEIGHT : 0
  });
  const [PMax, setPMax] = useState(0);
  const [leftThumb, setLeftThumb] = useState(0);

  useEffect(() => setTime(isSeeking ? seekTime : currentTimeSec), [isSeeking, currentTimeSec, seekTime]);
  useEffect(
    () => spritesheets.images && setSpritesheetLength(SpritesheetUtils.computeSpritesheetLength(spritesheets, duration)),
    [spritesheets, duration]
  );

  useEffect(() => setComputedBuffer(buffered), [computedDuration]);

  const cursorSize = (small || extraSmall) ? CURSOR_SIZE_EXTRA_SMALL : CURSOR_SIZE;
  const containerMargin = extraSmall ? 0 : (cursorSize / 2) + 5;

  const programVisualWidth = setStyles(EPG_PROGRAM_VISUAL_SIZE).width;
  const programVisualSize = { width: programVisualWidth, height: programVisualWidth / (16 / 9) };

  const onSlide = (pos, max) => {
    const progress = (pos / max);
    const maxPos = isTimeshiftingAuto || hasHighlights ? max : (duration / computedDuration) * max;
    const nextSeekTime = computeSeekTime(progress, duration, computedDuration, timeshifting);
    setSeekTime(nextSeekTime);
    setSeekTimePosition(pos > maxPos ? maxPos : pos);
    setPMax(maxPos);

    const { images = [] } = spritesheets;

    if (images.length || imagesEpg.length) {
      setThumbnail(
        isTimeshiftingAuto
          ? findCurrentProgram({
            seekTime: nextSeekTime,
            width: programVisualSize.width,
            height: programVisualSize.height,
            imagesEpg
          })
          : SpritesheetUtils.computeThumbnail({
            progress,
            spritesheets,
            spritesheetLength,
            ...thumbnailSize
          })
      );
    }

    if (isSeeking) player.seek(nextSeekTime, true);
  };

  const hoverPosition = useMemo(
    () => computeSeekPos(duration, computedDuration, timeshifting, seekTime, seekTime),
    [duration, computedDuration, timeshifting, seekTime]
  );

  useEffect(
    () => {
      const size = isTimeshiftingAuto || hasHighlights
        ? { width: programVisualSize.width, height: programVisualSize.height }
        : SpritesheetUtils.getThumbnailSizeFromSpritesheets(spritesheets);
      return thumbnail?.url && setThumbnailSize(size);
    },
    [thumbnail?.url]
  );

  useEffect(() => {
    if (UIVisible && hoverTimeline) {
      const halfWidth = thumbnailSize.width / 2;
      const commonPosition = seekTimePosition - halfWidth;
      const rightLimit = (seekTimePosition + halfWidth) < PMax ? commonPosition : (PMax - thumbnailSize.width);

      setLeftThumb(commonPosition < 0 ? 0 : rightLimit);
    }
  }, [UIVisible, hoverPosition, hoverTimeline]);

  // In case of timeshifting auto, the slider should represent the 4 hour window,
  // so take the current delay to the live head, and substract it to 4h.s
  const value = ((isTimeshiftingAuto ? (TIMESHIFTING_AUTO_DURATION - (duration - time)) : time) / computedDuration) * 100;

  useEffect(() => {
    if (programMarkers.length) {
      const markers = formatMarkers(programMarkers);
      const index = markers.findIndex((seg) => asPercent(seg, TIMESHIFTING_AUTO_DURATION) > (100 - value));
      setCurrentIndex(index);
      setCurrentSegment(index === -1 ? 0 : (markers.length - index));
      setSegments(computeEPGSegmentsWidths(markers));
    }
  }, [duration, value]);

  const useEpgTimeline = isTimeshiftingAuto && !isEPGDirty && !!segments.length;
  const isFingerUI = displayType === FINGER_UI;

  if (hidden) return null;

  return (
    <div style={setStyles(TIMELINE_WRAPPER_STYLE)} id="ftv-magneto--ui-timeline">
      <Position>
        <TimeDisplay
          time={timeshifting ? broadcastedAt : time}
          small={small}
          displayFn={timeshifting ? dateToHhS : secondsToHHMMSS}
          hidden={extraSmall || isTimeshiftingAuto}
          style={timeshifting ? setStyles(TIMELINE_TIME_HOUR_STYLE) : timeDisplayStyles}
        />
        <Slider
          multiSegments={useEpgTimeline}
          maxValue={computedDuration}
          value={value}
          /* buffer is used in two way : for the buffering stream and for the live stream */
          buffer={timeshifting ? 100 : computedBuffer}
          touch={mobile}
          strokeSize={STROKE_SIZE}
          bigStrokeSize={BIG_STROKE_SIZE}
          cursorSize={cursorSize}
          onStartSlide={() => !pinsHovered && onStartSlide(player)}
          onSlide={onSlide}
          onStopSlide={(pos, max) => !pinsHovered && onStopSlide({
            pos, max, duration, computedDuration, timeshifting, player, startOverTimeshifting, imagesHL
          })}
          onMouseEnter={() => setHoverTimeline({ store: player.store, hoverTimeline: true, dialogOpened: dialogCtx.dialogOpened })}
          onFocus={() => setHoverTimeline({ store: player.store, hoverTimeline: false, dialogOpened: dialogCtx.dialogOpened })}
          onMouseLeave={() => setHoverTimeline({ store: player.store, hoverTimeline: false, dialogOpened: dialogCtx.dialogOpened })}
          onBlur={() => setHoverTimeline({ store: player.store, hoverTimeline: false, dialogOpened: dialogCtx.dialogOpened })}
          onMouseMove={(pos, maxPos) => (!isSeeking && onSlide(pos, maxPos))}
          slices={segments}
          currentSegment={currentSegment}
          currentIndex={currentIndex}
          styles={{
            container: {
              flexGrow: 1,
              marginLeft: !isTimeshiftingAuto || timeshifting === null ? containerMargin : 0,
              marginRight: !timeshifting ? containerMargin : 0,
              position: 'relative'
            }
          }}
          primaryTrackStyles={{ ...resolveprimaryTrackStyles(timeshifting, startOverTimeshifting) }}
          secondaryTrackStyles={{ backgroundColor: timeshifting ? SLIDER_TIMELINE_LIVE_COLOR : SLIDER_BUFFERED_COLOR, left: '0px' }}
          sliderStyle={{ ...resolveSliderStyle(timeshifting, startOverTimeshifting) }}
          name="slider-timeline"
          disableFocus={false}
          accessibleName="curseur de progression"
          ariaValueText={!timeshifting
            ? `${timeToText(time)} sur ${timeToText(duration)}`
            : ''}
          trackSize={setStyles({ default: { height: 8 }, extraSmall: { height: 6 } })}
        >

          {/* MAIN TIMELINE TOOLTIP */}
          {!isFingerUI && !pinsHovered && (!useEpgTimeline || !isTimeshiftingAuto || isTimeshiftingManuel) && (
            <Tooltip
              withBackgroundPosition
              hidden={extraSmall}
              hover={hoverTimeline}
              isSliding={isSeeking}
              thumbnail={thumbnail}
              dimensions={{
                thumbnail: thumbnailSize,
                tooltip: tooltipSize
              }}
              label={useMemo(
                () => resolveBubbleLabel(seekTime, duration, timeshifting),
                [seekTime, timeshifting, duration]
              )}
              bubblePosition={{
                left: leftThumb,
                ...setStyles({ default: { top: -19 }, large: { top: -26 }, medium: { top: -22 } })
              }}
              // Hide hover seek at 3% before duration to avoid overlay on direct label and seek tooltip
              style={{ ...(timeshifting && hoverPosition >= MAX_TIMELINE_VALUE_SHOW ? { opacity: 0 } : {}) }}
            />
          )}

          {/* EPG Program Visual */}
          {!isFingerUI && !pinsHovered && hoverTimeline && isTimeshiftingAuto && useEpgTimeline && (
            <Tooltip
              hidden={extraSmall}
              live
              hover={hoverTimeline}
              isSliding={isSeeking}
              thumbnail={thumbnail}
              dimensions={{
                thumbnail: thumbnailSize,
                tooltip: tooltipSize
              }}
              type={TYPE_AUTO}
              bubblePosition={{ left: leftThumb }}
              style={setStyles(TOOLTIP_AUTO_CONTAINER_STYLES)}
            >
              <Titles
                title={thumbnail.title}
                containerStyle={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                subTitle={thumbnail.additionalTitle}
                titleStyle={setStyles(SPRITES_METADATA_TITLE_PROGRAM_STYLES)}
                subTitleStyle={setStyles(SPRITES_METADATA_TITLE_VIDEO_STYLES)}
              />
            </Tooltip>
          )}

          {/* LIVE POSITION INDICATORS  */}
          {!isFingerUI && timeshifting && (
            <LiveCursor /* KNOB */
              position={{
                ...{ left: duration > expectedStartOverDuration ? '100%' : `${((duration / expectedStartOverDuration) * 100)}%` },
                ...(timeshifting && !startOverTimeshifting ? { left: isTimeshiftingAuto ? 'calc(100% - 1px)' : '100%' } : {})
              }}
              strokeSize={STROKE_SIZE}
              cursorSize={cursorSize}
              onClick={() => (startOverTimeshifting && player.backToLive(TIMESHIFTING_TYPE_AUTO
                ? TIMESHIFTING_AUTO_CONTROLLER_NAME
                : TIMESHIFTING_CONTROLLER_NAME)
              )}
              style={{
                ...(startOverTimeshifting
                  ? {
                    backgroundColor: SLIDER_TIMELINE_LIVE_COLOR,
                    cursor: 'pointer',
                    pointerEvents: 'auto'
                  }
                  : {})
              }}
              live
              timeshifting={(isTimeshiftingAuto && useEpgTimeline)}
            />
          )}

          {!isFingerUI && hasHighlights && !extraSmall && (
            <Pins
              pins={pinsPosition}
              duration={duration}
            />
          )}
        </Slider>
        <TimeDisplay
          time={computedDuration}
          small={small}
          hidden={timeshifting || extraSmall}
          style={timeDisplayStyles}
        />
      </Position>
    </div>
  );
}

export default withBreakPoints(Timeline);
